import { TypedDocumentNode, gql } from '@apollo/client';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { UserCheck } from '@sorare/core/src/atoms/icons/UserCheck';
import { UserXMark } from '@sorare/core/src/atoms/icons/UserXMark';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { StatDisplay } from '@sorare/core/src/atoms/ui/StatDisplay';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { RewardBoostApplied } from 'components/fixture/FixtureTeamsOverview/RewardBoostApplied';

import { UpcomingFixtureLeague } from './UpcomingFixtureLeague';
import { UpcomingFixtureOverview_so5Fixture } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  @media ${laptopAndAbove} {
    min-width: 420px;
  }
`;
const Header = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: baseline;

  & > *:first-child {
    grid-column-start: 2;
  }
  & > *:nth-child(2) {
    grid-column-start: 3;
    margin-left: auto;
  }
`;
const Counts = styled(Horizontal)`
  justify-content: space-evenly;
`;

type Props = {
  so5Fixture: UpcomingFixtureOverview_so5Fixture;
  onClose?: () => void;
  scrollToContender: (contender: string) => void;
};

export const UpcomingFixtureOverview = ({
  so5Fixture,
  onClose,
  scrollToContender,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();
  const { set: contendersSet = [], notset: contendersNotSet = [] } = groupBy(
    so5Fixture.so5LeaderboardGroups.flatMap(
      so5League => so5League.mySo5LeaderboardContenders
    ),
    contender => (contender.so5Lineup?.confirmable ? 'set' : 'notset')
  );

  const { my: myLeagues = [], new: newLeagues = [] } = groupBy(
    so5Fixture.so5LeaderboardGroups,
    league => (league.mySo5LeaderboardContenders.length ? 'my' : 'new')
  );

  const hasRewardBoostApplied = myLeagues.some(league =>
    league.mySo5LeaderboardContenders.some(
      contender => (contender.so5Lineup?.rewardMultiplier || 1) > 1
    )
  );

  return (
    <Root gap={6}>
      {!isLaptopAndAbove && (
        <Header>
          <BodyS bold className="text-center">
            {so5Fixture.displayName}
          </BodyS>
          <IconButton
            icon={faTimes}
            color="transparent"
            smaller
            onClick={onClose}
          />
        </Header>
      )}
      <Counts>
        <StatDisplay
          size={56}
          icon={<UserCheck size={4} gradient="green" />}
          label={
            <FormattedMessage
              id="UpcomingFixtureOverview.set"
              defaultMessage="Set"
            />
          }
        >
          {contendersSet.length}
        </StatDisplay>
        <StatDisplay
          size={56}
          icon={<UserXMark size={4} gradient="red" />}
          label={
            <FormattedMessage
              id="UpcomingFixtureOverview.notSet"
              defaultMessage="Not Set"
            />
          }
        >
          {contendersNotSet.length}
        </StatDisplay>
      </Counts>
      {hasRewardBoostApplied && (
        <Vertical center>
          <RewardBoostApplied />
        </Vertical>
      )}
      {!!myLeagues.length && (
        <Vertical gap={1}>
          <BodyS bold color="var(--c-nd-600)">
            <FormattedMessage
              id="UpcomingFixtureOverview.myTeams"
              defaultMessage="My Teams"
            />
          </BodyS>
          <Vertical gap={0}>
            {myLeagues.map(league => (
              <UpcomingFixtureLeague
                key={league.slug}
                so5LeaderboardGroupInterface={league}
                scrollToContender={contender => {
                  onClose?.();
                  scrollToContender(contender);
                }}
              />
            ))}
          </Vertical>
        </Vertical>
      )}
      {!!newLeagues.length && (
        <Vertical gap={1}>
          <BodyS bold color="var(--c-nd-600)">
            <FormattedMessage
              id="UpcomingFixtureOverview.newCompetitions"
              defaultMessage="New Competitions"
            />
          </BodyS>
          <Vertical gap={0}>
            {newLeagues.map((league, i) => (
              <UpcomingFixtureLeague
                key={league.slug}
                so5LeaderboardGroupInterface={league}
                hideBorder={i === newLeagues.length - 1}
                scrollToContender={contender => {
                  onClose?.();
                  scrollToContender(contender);
                }}
              />
            ))}
          </Vertical>
        </Vertical>
      )}
    </Root>
  );
};

UpcomingFixtureOverview.fragments = {
  so5Fixture: gql`
    fragment UpcomingFixtureOverview_so5Fixture on So5Fixture {
      slug
      displayName
      so5LeaderboardGroups {
        slug
        mySo5LeaderboardContenders {
          slug
          so5Lineup {
            id
            confirmable
            rewardMultiplier
          }
        }
        ...UpcomingFixtureLeague_so5LeaderboardGroupInterface
      }
    }
    ${UpcomingFixtureLeague.fragments.so5LeaderboardGroupInterface}
  ` as TypedDocumentNode<UpcomingFixtureOverview_so5Fixture>,
};
